import React, { useMemo } from "react"
import { Chart, Polygon, Coordinate, Tooltip, Axis } from "bizcharts"

const BizChartsHeatmap = ({
  graph = {},
  valueField = "value",
  colorField = "value",
}) => {
  const data = graph?.graphData || []
  const xAxisDetails = graph?.graphMetadata?.xAxisDetails || {}

  // --- Color palette and interpolation ---
  const PALETTE = [
    [13, 8, 134], // lighter blue-purple
    [145, 16, 161], // lighter purple
    [197, 65, 127], // light pink
    [244, 143, 70], // light orange
    [250, 215, 37], // light yellow
  ]

  // Interpolation function (used for both chart and legend)
  const interpolateColor = normalized => {
    const colors = PALETTE
    const steps = colors.length - 1
    const idx = Math.floor(normalized * steps)
    const t = normalized * steps - idx

    const c1 = colors[idx]
    const c2 = colors[Math.min(idx + 1, steps)]

    const r = Math.round(c1[0] + (c2[0] - c1[0]) * t)
    const g = Math.round(c1[1] + (c2[1] - c1[1]) * t)
    const b = Math.round(c1[2] + (c2[2] - c1[2]) * t)

    return `rgb(${r},${g},${b})`
  }

  // Heatmap color function (used for chart)
  const getSyncedColor = (value, minValue, maxValue) => {
    if (maxValue === minValue) return PALETTE[0]
    const normalized = (value - minValue) / (maxValue - minValue)
    return interpolateColor(normalized)
  }

  // --- Color legend component ---
  const ColorLegend = ({ minValue, maxValue }) => {
    const legendSteps = 11
    const steps = Array.from({ length: legendSteps }, (_, i) => {
      const ratio = i / (legendSteps - 1)
      return {
        color: interpolateColor(ratio),
        value: minValue + (maxValue - minValue) * ratio,
      }
    })

    const labelSteps = [0, 2, 4, 6, 8, 10]

    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          fontSize: 11,
          marginTop: "-2px",
        }}
      >
        {/* Gradient Bar */}
        <div
          style={{
            display: "flex",
            height: "20px",
            border: "1px solid #ddd",
            borderRadius: "3px",
            overflow: "hidden",
            marginBottom: "3px",
          }}
        >
          {steps.map((step, index) => (
            <div
              key={index}
              style={{
                width: "25px",
                height: "100%",
                backgroundColor: step.color,
              }}
            />
          ))}
        </div>

        {/* Value Labels */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: `${25 * legendSteps}px`,
            position: "relative",
            bottom: 6,
          }}
        >
          {labelSteps.map(stepIndex => {
            const step = steps[stepIndex]
            const position = (stepIndex / (legendSteps - 1)) * 100

            return (
              <div
                key={stepIndex}
                style={{
                  position: "absolute",
                  left: `${position}%`,
                  transform: "translateX(-50%)",
                  color: "#666",
                  fontWeight: "500",
                  whiteSpace: "nowrap",
                }}
              >
                {step.value.toFixed(1)}
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const chartData = useMemo(() => {
    // Since data is already in flat format, no transformation needed
    let processedData = data

    // Only apply time formatting if needed
    if (xAxisDetails.type && processedData.length > 0) {
      processedData = processedData.map(item => {
        let processedXValue = item.xAxisValue

        if (xAxisDetails.type === "time" && item.xAxisValue) {
          const date = new Date(item.xAxisValue)
          if (!isNaN(date.getTime())) {
            switch (xAxisDetails.granularity) {
              case "Seconds":
                processedXValue = date.toLocaleString("en-US", {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                  hour12: false,
                })
                break
              case "Minutes":
                processedXValue = date.toLocaleString("en-US", {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                break
              case "Hours":
                processedXValue = date.toLocaleString("en-US", {
                  month: "short",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                })
                break
              case "Days":
                processedXValue = date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                })
                break
              case "Weeks":
                processedXValue = date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                })
                break
              case "Months":
                processedXValue = date.toLocaleDateString("en-US", {
                  month: "short",
                  year: "numeric",
                })
                break
              default:
                processedXValue = date.toLocaleDateString("en-US", {
                  month: "short",
                  day: "2-digit",
                })
            }
          }
        }

        return {
          ...item,
          xAxisValue: processedXValue,
        }
      })
    }

    return processedData
  }, [data, xAxisDetails.type, xAxisDetails.granularity])

  const { minValue, maxValue } = useMemo(() => {
    if (!chartData.length) return { minValue: 0, maxValue: 100 }

    const values = chartData
      .map(item => item[valueField])
      .filter(val => val != null && !isNaN(val))

    return {
      minValue: Math.min(...values),
      maxValue: Math.max(...values),
    }
  }, [chartData, valueField])

  const displayData = chartData
  const displayMinValue = chartData.length > 0 ? minValue : 0
  const displayMaxValue = chartData.length > 0 ? maxValue : 1

  if (!displayData.length) {
    return (
      <div
        style={{
          height: 330,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#999",
          width: "100%",
        }}
      >
        No data available
      </div>
    )
  }

  return (
    <div
      style={{
        width: "100%",
        height: "auto",
        minHeight: 330,
      }}
    >
      <Chart
        data={displayData}
        autoFit={true}
        padding={[10, 10, 62, 90]}
        height={330}
        forceFit={true}
      >
        <Coordinate type="rect" />

        {/* X Axis */}
        <Axis
          name="xAxisValue"
          position="bottom"
          label={{
            textAlign: "center",
            style: {
              fontSize: 11,
              fill: "#333",
            },
            offset: 15,
          }}
          line={{ style: { stroke: "#e8e8e8" } }}
          tickLine={{ style: { stroke: "#e8e8e8" } }}
        />

        {/* Y Axis */}
        <Axis
          name="yAxisValue"
          position="left"
          label={{
            style: {
              fontSize: 11,
              fill: "#333",
            },
            offset: 10,
          }}
          line={{ style: { stroke: "#e8e8e8" } }}
          tickLine={{ style: { stroke: "#e8e8e8" } }}
        />

        {/* Tooltip */}

        <Tooltip
          showTitle={false}
          containerTpl='<div class="g2-tooltip" style="position: absolute; visibility: hidden; background-color: rgba(255, 255, 255, 0.95); box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); border-radius: 4px; padding: 8px; font-size: 12px;"><table class="g2-tooltip-list"></table></div>'
          itemTpl='<tr class="g2-tooltip-list-item"><td style="color: {color}; padding: 2px 8px 2px 0;">{name}</td><td style="padding: 2px 0;">{value}</td></tr>'
        />

        {/* Heatmap Polygons with Labels */}
        <Polygon
          position="xAxisValue*yAxisValue"
          color={[colorField, val => getSyncedColor(val, minValue, maxValue)]}
          style={{
            stroke: "#fff",
            lineWidth: 1,
            cursor: "pointer",
          }}
          tooltip={[
            `xAxisValue*yAxisValue*${valueField}`,
            (xVal, yVal, val) => ({
              name: `${yVal} - ${xVal}`,
              value: val.toFixed(1),
            }),
          ]}
          label={[
            valueField,
            {
              content: data => {
                return data[valueField].toFixed(1)
              },
              style: {
                fill: "#fff",
                fontSize: 11,
                fontWeight: "bold",
                textAlign: "center",
                textBaseline: "middle",
              },
            },
          ]}
        />
      </Chart>

      <ColorLegend minValue={displayMinValue} maxValue={displayMaxValue} />
    </div>
  )
}

BizChartsHeatmap.defaultProps = {
  graph: {},
  valueField: "value",
  colorField: "value",
}

export default BizChartsHeatmap
